import { findIndex, uid } from "@dhx/ts-common/core";
import { IEventSystem } from "@dhx/ts-common/events";
import { DataCollection } from "./datacollection";
import { DataProxy } from "./dataproxy";
import { toDataDriver, isTreeCollection, copyWithoutInner, dhxError } from "./helpers";
import {
	DataCallback,
	DataEvents,
	IDataCollection,
	IDataItem,
	ITreeCollection,
	TreeFilterType,
	IFilterCallback,
	IFilterMode,
	IFilterComplexMode,
	ITreeFilterConfig,
	DataDriver,
	ISortMode,
	IDataDriver,
} from "./types";
import { IAnyObj, Id } from "@dhx/ts-common/types";

function addToOrder(store: any, obj: object, parent: Id, index: number) {
	if (index !== undefined && index !== -1 && store[parent] && store[parent][index]) {
		store[parent].splice(index, 0, obj);
	} else {
		if (!store[parent]) {
			store[parent] = [];
		}
		store[parent].push(obj);
	}
}

export class TreeCollection<T extends IDataItem = IDataItem> extends DataCollection<T>
	implements ITreeCollection<T> {
	protected _childs: { [id: string]: T[] } = {};
	protected _root: Id;
	protected _filters: {
		filters: IFilterComplexMode | IFilterCallback;
		config: ITreeFilterConfig;
	};

	private _initChilds: { [id: string]: T[] };

	constructor(config?: any, events?: IEventSystem<DataEvents>) {
		super(config, events);
		const root = (this._root = (config && config.rootId) || "_ROOT_" + uid());
		this._childs = { [root]: [] };
		this._initChilds = null;
	}

	add(newItem: IDataItem, index?: number, parent?: Id): Id;
	add(newItem: IDataItem[], index?: number, parent?: Id): Id[];
	add(newItem: IDataItem | IDataItem[], index = -1, parent: Id = this._root): Id | Id[] {
		if (!this.events.fire(DataEvents.beforeAdd, [newItem])) {
			return;
		}
		if (typeof newItem !== "object") {
			newItem = {
				value: newItem,
			};
		}
		if (Array.isArray(newItem)) {
			return newItem.map((element, key) => {
				return this._add(element, index, parent, key);
			});
		} else {
			return this._add(newItem, index, parent);
		}
	}
	getRoot(): Id {
		return this._root;
	}
	getParent(id: Id, asObj = false): Id {
		if (!this._pull[id]) {
			return null;
		}
		const parent = this._pull[id].parent;
		return asObj ? this._pull[parent] : parent;
	}
	getItems(id: Id): T[] {
		if (this._childs && this._childs[id]) {
			return this._childs[id];
		}
		return [];
	}
	getLength(id: Id = this._root): number {
		if (!this._childs[id]) {
			return null;
		}
		return this._childs[id].length;
	}
	removeAll(id?: Id): void {
		if (!id) {
			super.removeAll();
			const root = this._root;
			this._initChilds = null;
			this._childs = { [root]: [] };
		} else if (this._childs[id]) {
			const childs = [...this._childs[id]];
			for (const child of childs) {
				this.remove(child.id);
			}
		}
	}
	getIndex(id: Id): number {
		const parent = this.getParent(id);
		if (!parent || !this._childs[parent]) {
			return -1;
		}
		return findIndex(this._childs[parent], item => item.id === id);
	}
	sort(rule?: ISortMode): void {
		if (!rule) {
			this._childs = {};
			// [dirty]
			this._parse_data(Object.keys(this._pull).map(key => this._pull[key]));
			if (this._filters) this.filter(this._filters.filters, this._filters.config);
		} else {
			for (const key in this._childs) {
				this._sort.sort(this._childs[key], rule);
			}
			if (this._initChilds && Object.keys(this._initChilds).length) {
				for (const key in this._initChilds) {
					this._sort.sort(this._initChilds[key], rule);
				}
			}
		}

		this.events.fire(DataEvents.change);
	}
	filter(rule?: IFilterMode | IFilterComplexMode | IFilterCallback, config: ITreeFilterConfig = {}): void {
		if (!rule) {
			this.restoreOrder();
			return;
		}

		if (!this._initOrder) {
			this._initOrder = this._order;
		}

		if (!this._initChilds) {
			this._initChilds = this._childs;
		}
		config.type = config.type || TreeFilterType.all;

		this._filters = {
			filters: {},
			config: config,
		};
		if (typeof rule !== "function") {
			if (rule["by"]) {
				this._filters.filters[rule.by as string] = rule;
			} else {
				for (const compare in rule) {
					this._filters.filters[compare] = rule[compare];
				}
			}
		} else {
			this._filters.filters = rule;
		}

		const newChilds = {};
		this._recursiveFilter(this._filters.filters, config, this._root, 0, newChilds);

		Object.keys(newChilds).forEach(key => {
			let parentId = this.getParent(key);
			let current = this.getItem(key);
			while (parentId) {
				if (!newChilds[parentId]) {
					newChilds[parentId] = [];
				}

				if (current && !newChilds[parentId].find(x => x.id === current.id)) {
					newChilds[parentId].push(current);
				}
				current = this.getItem(parentId);
				parentId = this.getParent(parentId);
			}
		});

		this._childs = newChilds;

		this.events.fire(DataEvents.change);
	}
	restoreOrder(): void {
		if (this._initChilds) {
			this._childs = this._initChilds;
			this._initChilds = null;
		}

		this.events.fire(DataEvents.change);
	}
	copy(id: Id, index: number, target?: IDataCollection | ITreeCollection, targetId?: Id): Id;
	copy(id: Id[], index: number, target?: IDataCollection | ITreeCollection, targetId?: Id): Id[];
	copy(
		id: Id | Id[],
		index: number,
		target: IDataCollection | ITreeCollection = this,
		targetId: Id = this._root
	): Id | Id[] {
		if (id instanceof Array) {
			return id.map((elementId, key) => {
				return this._copy(elementId, index, target, targetId, key);
			});
		} else {
			return this._copy(id, index, target, targetId);
		}
	}
	move(id: Id, index: number, target?: ITreeCollection | IDataCollection, targetId?: Id): Id;
	move(id: Id[], index: number, target?: ITreeCollection | IDataCollection, targetId?: Id): Id[];
	move(
		id: Id | Id[],
		index: number,
		target: ITreeCollection | IDataCollection = this,
		targetId: Id = this._root
	): Id | Id[] {
		if (id instanceof Array) {
			return id.map((elementId, key) => {
				return this._move(elementId, index, target, targetId, key);
			});
		} else {
			return this._move(id, index, target, targetId);
		}
	}
	forEach(callback: DataCallback<any>, parent: Id = this._root, level = Infinity): void {
		if (!this.haveItems(parent) || level < 1) {
			return;
		}
		const array = this._childs[parent];
		for (let i = 0; i < array.length; i++) {
			callback.call(this, array[i], i, array);
			if (this.haveItems(array[i].id)) {
				this.forEach(callback, array[i].id, --level);
			}
		}
	}
	eachChild(
		id: Id,
		callback: DataCallback<T>,
		direct = true,
		checkItem: (item: IDataItem) => boolean = () => true
	): void {
		if (!this.haveItems(id)) {
			return;
		}
		for (let i = 0; i < this._childs[id].length; i++) {
			callback.call(this, this._childs[id][i], i);
			if (direct && checkItem(this._childs[id][i])) {
				this.eachChild(this._childs[id][i].id, callback, direct, checkItem);
			}
		}
	}
	getNearId(id: Id): Id {
		return id; // for selection
	}
	loadItems(id: Id, driver: IDataDriver | DataDriver = DataDriver.json): void {
		const url = this.config.autoload + "?id=" + id;
		const proxy = new DataProxy(url);
		proxy.load().then(data => {
			driver = toDataDriver(driver);
			data = driver.toJsonArray(data) as any;
			this._parse_data(data, id);

			this.events.fire(DataEvents.change);
		});
	}
	refreshItems(id: Id, driver: IDataDriver | DataDriver = DataDriver.json): void {
		this.removeAll(id);
		this.loadItems(id, driver);
	}
	eachParent(id: Id, callback: DataCallback<T>, self = false): void {
		const item = this.getItem(id);
		if (!item) {
			return;
		}
		if (self) {
			callback.call(this, item);
		}
		if (item.parent === this._root) {
			return;
		}
		const parent = this.getItem(item.parent);
		callback.call(this, parent);
		this.eachParent(item.parent, callback);
	}
	haveItems(id: Id): boolean {
		return id in this._childs;
	}
	canCopy(id: Id, target: Id): boolean {
		if (id === target) {
			return false;
		}
		let canCopy = true;
		this.eachParent(target, item => (item.id === id ? (canCopy = false) : null)); // locate return string
		return canCopy;
	}
	serialize(driver: DataDriver = DataDriver.json, checkItem?: (item: any) => any) {
		const data = this._serialize(this._root, checkItem);
		const dataDriver = toDataDriver(driver);
		if (dataDriver) {
			return dataDriver.serialize(data);
		}
	}
	getId(index: number, parent: Id = this._root): Id {
		if (!this._childs[parent] || !this._childs[parent][index]) {
			return;
		}
		return this._childs[parent][index].id;
	}
	// Non public API from suite_6.4
	map(callback: DataCallback<T>, parent: Id = this._root, direct = true): any[] {
		let result: any[] = [];
		if (!this.haveItems(parent)) {
			return result;
		}
		for (let i = 0; i < this._childs[parent].length; i++) {
			result.push(callback.call(this, this._childs[parent][i], i, this._childs));
			if (direct) {
				const childResult = this.map(callback, this._childs[parent][i].id, direct);
				result = result.concat(childResult);
			}
		}
		return result;
	}

	getRawData(from: number, to: number, order?: T[], mode?: number, parent?: Id): T[] {
		parent = parent || this._root;
		let out: T[];
		if (!this._childs[parent]) return [];
		if (parent === this._root) out = super.getRawData(from, to, this._childs[parent]);
		else out = this._childs[parent];

		if (mode === 2) {
			return this.flatten(out);
		}
		return out;
	}
	protected flatten(input: T[]): T[] {
		let out = [];
		input.forEach(a => {
			out.push(a);
			const kids = this._childs[a.id];
			if (kids && a.$opened) {
				out = out.concat(this.flatten(kids));
			}
		});

		return out;
	}
	protected _add(newItem: IDataItem, index = -1, parent: Id = this._root, key?: number): Id {
		newItem.parent = newItem.parent ? newItem.parent.toString() : parent;
		if (key > 0 && index !== -1) {
			index = index + 1;
		}
		const id = super._add(newItem, index);

		if (Array.isArray(newItem.items)) {
			for (const item of newItem.items) {
				this.add(item, -1, newItem.id);
			}
		}
		return id;
	}
	protected _copy(
		id: Id,
		index: number,
		target: IDataCollection | ITreeCollection = this,
		targetId: Id = this._root,
		key?: number
	): Id {
		if (!this.exists(id)) {
			return null;
		}
		const currentChilds = this._childs[id];
		if (key) {
			index = index === -1 ? -1 : index + key;
		}
		if (target === this && !this.canCopy(id, targetId)) {
			return null;
		}
		const itemCopy = copyWithoutInner(this.getItem(id), { items: true });
		if (target.exists(id)) {
			itemCopy.id = uid();
		}
		if (!isTreeCollection(target)) {
			target.add(itemCopy, index);
			return;
		}
		if (this.exists(id)) {
			itemCopy.parent = targetId;
			if (target !== this && targetId === this._root) {
				itemCopy.parent = target.getRoot();
			}
			target.add(itemCopy, index);
			id = itemCopy.id;
		}
		if (currentChilds) {
			for (const child of currentChilds) {
				const childId = child.id;
				const childIndex = this.getIndex(childId);
				if (typeof id === "string") {
					this.copy(childId, childIndex, target, id);
				}
			}
		}
		return id;
	}
	protected _move(
		id: Id,
		index: number,
		target: ITreeCollection | IDataCollection = this,
		targetId: Id = this._root,
		key?: number
	): Id {
		if (!this.exists(id)) {
			return null;
		}
		if (key) {
			index = index === -1 ? -1 : index + key;
		}
		if (target !== this) {
			if (!isTreeCollection(target)) {
				// move to datacollection
				target.add(copyWithoutInner(this.getItem(id)), index);
				this.remove(id);
				return;
			}
			const returnId = this.copy(id, index, target, targetId);
			this.remove(id);
			return returnId;
		}
		// move inside
		if (!this.canCopy(id, targetId)) {
			return null;
		}
		const parent = this.getParent(id);
		const parentIndex = this.getIndex(id);

		// get item from parent array and move to target array
		const spliced = this._childs[parent].splice(parentIndex, 1)[0];
		(spliced as IDataItem).parent = targetId; // need for next moving, ... not best solution, may be full method for get item

		if (!this._childs[parent].length) {
			delete this._childs[parent];
		}
		if (!this.haveItems(targetId)) {
			this._childs[targetId] = [];
		}
		if (index === -1) {
			index = this._childs[targetId].push(spliced);
		} else {
			this._childs[targetId].splice(index, 0, spliced);
		}

		this.events.fire(DataEvents.change, [id, "update", this.getItem(id)]);

		return id;
	}
	protected _reset(id?: Id) {
		if (id) {
			const childs = [...this._childs[id]];
			for (const child of childs) {
				this.remove(child.id);
			}
		} else {
			super._reset();
			const root = this._root;
			this._initChilds = null;
			this._childs = { [root]: [] };
		}
	}
	protected _removeCore(id) {
		if (this._pull[id]) {
			const parent = this.getParent(id);
			this._childs[parent] = this._childs[parent].filter(item => item.id !== id);
			if (parent !== this._root && !this._childs[parent].length) {
				delete this._childs[parent];
			}
			if (this._initChilds && this._initChilds[parent]) {
				this._initChilds[parent] = this._initChilds[parent].filter(item => item.id !== id);
				if (parent !== this._root && !this._initChilds[parent].length) {
					delete this._initChilds[parent];
				}
			}
			if (this._initOrder && this._initOrder.length) {
				this._initOrder = this._initOrder.filter(el => el.id !== id);
			}
			this._fastDeleteChilds(this._childs, id);
			if (this._initChilds) {
				this._fastDeleteChilds(this._initChilds, id);
			}
		}
	}
	protected _addToOrder(_order, obj: any, index: number): void {
		const childs = this._childs;
		const initChilds = this._initChilds;
		const parent = obj.parent;
		this._pull[obj.id] = obj;

		if (
			obj.parent &&
			this._pull[obj.parent] &&
			this._pull[obj.parent].items &&
			!this._pull[obj.parent].items.find(item => item.id === obj.id)
		) {
			this._pull[obj.parent].items.push(obj);
		}

		super._addToOrder(_order, obj, index);

		addToOrder(childs, obj, parent, index);
		if (initChilds) {
			addToOrder(initChilds, obj, parent, index);
		}
	}
	protected _parse_data(data: any, parent = this._root) {
		let index = this._order.length;
		for (let obj of data) {
			if (this.config.init) {
				obj = this.config.init(obj);
			}
			if (obj && typeof obj !== "object") {
				obj = {
					value: obj,
				};
			}

			obj.id = obj.id ?? uid();
			obj.parent =
				typeof obj.parent === "undefined" || obj.parent === null || (obj.parent && obj.$items)
					? parent
					: obj.parent;

			if (this._pull[obj.id]) {
				dhxError(`Item ${obj.id} already exist`);
			}

			this._pull[obj.id] = obj;
			this._order[index++] = obj;
			if (!this._childs[obj.parent]) {
				this._childs[obj.parent] = [];
			}
			this._childs[obj.parent].push(obj);
			if (obj.items && obj.items instanceof Object) {
				this._parse_data(obj.items, obj.id);
			}
		}
	}
	private _fastDeleteChilds(target, id: Id): void {
		if (this._pull[id]) {
			delete this._pull[id];
		}
		if (!target[id]) {
			return;
		}
		for (let i = 0; i < target[id].length; i++) {
			this._fastDeleteChilds(target, target[id][i].id);
		}
		delete target[id];
	}
	private _recursiveFilter(
		rule: IFilterComplexMode | IFilterCallback,
		config: ITreeFilterConfig,
		current: Id,
		level: number,
		newChilds: IAnyObj
	): void {
		const childs = this._childs[current];
		if (!childs) {
			return;
		}
		const condition = (item: T): boolean => {
			switch (config.type) {
				case TreeFilterType.all: {
					return true;
				}
				case TreeFilterType.level: {
					return level === config.level;
				}
				case TreeFilterType.leafs: {
					return !this.haveItems(item.id);
				}
			}
		};
		if (typeof rule === "function") {
			const customRule = (item: T) => condition(item) && rule(item);
			const filtered = childs.filter(customRule);
			if (filtered.length) {
				newChilds[current] = filtered;
			}
		} else {
			const customRule = (item: T) => {
				let responseOfRule = true;
				for (const compare in rule) {
					if (rule[compare].by && rule[compare].match !== "") {
						responseOfRule =
							item[rule[compare].by] &&
							item[rule[compare].by]
								.toString()
								.toLocaleLowerCase()
								.indexOf(rule[compare].match.toString().toLowerCase()) !== -1;
					}
					if (!responseOfRule) break;
				}
				return condition(item) && responseOfRule;
			};
			const filtered = childs.filter(customRule);
			if (filtered.length) {
				newChilds[current] = filtered;
			}
		}
		for (const child of childs) {
			this._recursiveFilter(rule, config, child.id, level + 1, newChilds);
		}
	}
	private _serialize(parent = this._root, fn?) {
		return this.map(
			item => {
				let itemCopy: any = {};
				for (const key in item) {
					if (key === "parent" || key === "items" || key.startsWith("$")) {
						continue;
					}
					itemCopy[key] = item[key];
				}
				if (fn) {
					itemCopy = fn(itemCopy);
				}
				if (this.haveItems(item.id)) {
					itemCopy.items = this._serialize(item.id, fn);
				}
				return itemCopy;
			},
			parent,
			false
		);
	}
}
